import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { IndirectTaxService } from '../../services/indirectTax.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { Account } from '../../interfaces/accounting.interface';
import {
  TaxApplicability, TaxCategory, TaxCode, TaxComponentKind, TaxCodeSave, TaxDefaultsResult, TaxFilingFrequency, TaxProfile, TaxRegime,
} from '../../interfaces/indirectTax.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { postingAccounts } from '../../utils/accountHierarchy';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import SearchableSelect from '../common/SearchableSelect';
import { ConfirmDialog } from '../common/ConfirmDialog';

const CATEGORIES: TaxCategory[] = ['STANDARD', 'REDUCED', 'ZERO_RATED', 'EXEMPT', 'OUT_OF_SCOPE'];
const APPLICABILITIES: TaxApplicability[] = ['OUTPUT', 'INPUT', 'BOTH'];
const label = (v: string) => v.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());

export const componentSummary = (c: TaxCode): string => {
  if (!c.components || c.components.length === 0) return `${c.effectiveRatePct}%`;
  const parts = c.components.map(k => `${k.name} ${k.ratePct}%`);
  const compound = c.components.some((k, i) => i > 0 && k.compoundOnPrevious);
  return parts.join(' + ') + (compound ? ' (compound)' : '');
};

const EMPTY_PROFILE = (institutionId: string): TaxProfile => ({
  institutionId, regime: 'NETTING', countryCode: 'BD', filingFrequency: 'MONTHLY', registrationNo: '',
  defaultOutputCodeId: null, defaultInputCodeId: null, exportCodeId: null,
  settlementAccountId: null, refundableAccountId: null, nonRecoverableExpenseAccountId: null,
});

interface CompRow { name: string; ratePct: string; compoundOnPrevious: boolean; kind: TaxComponentKind; outputAccountId: string; inputAccountId: string }
const emptyComp = (): CompRow => ({ name: '', ratePct: '', compoundOnPrevious: false, kind: 'VAT', outputAccountId: '', inputAccountId: '' });

const TaxCodesPage: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution() as string;
  const canManage = usePermission(PERMISSIONS.INDIRECT_TAX_MANAGE);

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<TaxProfile>(EMPTY_PROFILE(institutionId));
  const [codes, setCodes] = useState<TaxCode[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [savingProfile, setSavingProfile] = useState(false);

  const [confirmDefaults, setConfirmDefaults] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [defaultsResult, setDefaultsResult] = useState<TaxDefaultsResult | null>(null);

  const [editing, setEditing] = useState<TaxCode | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', category: 'STANDARD' as TaxCategory, applicability: 'BOTH' as TaxApplicability, recoverable: true, note: '' });
  const [comps, setComps] = useState<CompRow[]>([emptyComp()]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<TaxCode | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [p, c, a] = await Promise.all([
        IndirectTaxService.getProfile(institutionId).catch(() => null),
        IndirectTaxService.getCodes(institutionId),
        AccountService.getAllAccounts().catch(() => [] as Account[]),
      ]);
      setProfile(p ? { ...EMPTY_PROFILE(institutionId), ...p, institutionId } : EMPTY_PROFILE(institutionId));
      setCodes(c ?? []);
      setAccounts(a ?? []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load tax codes');
    } finally {
      setLoading(false);
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const leaves = useMemo(() => postingAccounts(accounts), [accounts]);
  const liabilityAccts = leaves.filter(a => a.accountType === 'LIABILITY');
  const assetAccts = leaves.filter(a => a.accountType === 'ASSET');
  const expenseAccts = leaves.filter(a => a.accountType === 'EXPENSE');

  const setP = (patch: Partial<TaxProfile>) => setProfile(p => ({ ...p, ...patch }));

  const saveProfile = async () => {
    try {
      setSavingProfile(true);
      const saved = await IndirectTaxService.saveProfile({ ...profile, institutionId });
      setProfile({ ...EMPTY_PROFILE(institutionId), ...saved });
      toast.showSuccess('Tax profile saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save tax profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const runDefaults = async () => {
    try {
      setLoadingDefaults(true);
      const res = await IndirectTaxService.loadBangladeshDefaults(institutionId);
      setDefaultsResult(res);
      setConfirmDefaults(false);
      await load();
    } catch (e) {
      setConfirmDefaults(false);
      toast.showApiError(e, 'Failed to load Bangladesh defaults');
    } finally {
      setLoadingDefaults(false);
    }
  };

  const openDialog = (c: TaxCode | null) => {
    setEditing(c);
    if (c) {
      setForm({ code: c.code, name: c.name, category: c.category, applicability: c.applicability, recoverable: c.recoverable, note: c.note ?? '' });
      setComps(c.components.length ? c.components.map(k => ({
        name: k.name, ratePct: String(k.ratePct), compoundOnPrevious: k.compoundOnPrevious, kind: k.kind ?? 'VAT',
        outputAccountId: k.outputAccountId ?? '', inputAccountId: k.inputAccountId ?? '',
      })) : [emptyComp()]);
    } else {
      setForm({ code: '', name: '', category: 'STANDARD', applicability: 'BOTH', recoverable: true, note: '' });
      setComps([emptyComp()]);
    }
    setDialogOpen(true);
  };

  const setComp = (i: number, patch: Partial<CompRow>) => setComps(cs => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const formValid = form.code.trim() !== '' && form.name.trim() !== ''
    && comps.length > 0 && comps.every(c => c.name.trim() !== '' && c.ratePct !== '' && Number(c.ratePct) >= 0);

  const saveCode = async () => {
    const body: TaxCodeSave = {
      institutionId, code: form.code.trim(), name: form.name.trim(), category: form.category,
      applicability: form.applicability, recoverable: form.recoverable, note: form.note.trim() || null,
      components: comps.map((c, i) => ({
        name: c.name.trim(), ratePct: Number(c.ratePct), compoundOnPrevious: i > 0 && c.compoundOnPrevious, kind: c.kind,
        outputAccountId: c.outputAccountId || null, inputAccountId: c.inputAccountId || null,
      })),
    };
    try {
      setSaving(true);
      if (editing) await IndirectTaxService.updateCode(editing.id, body);
      else await IndirectTaxService.createCode(body);
      toast.showSuccess('Tax code saved');
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save tax code');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c: TaxCode) => {
    try {
      await IndirectTaxService.setCodeActive(c.id, !c.active);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to update tax code');
    }
  };

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await IndirectTaxService.deleteCode(deleting.id);
      toast.showSuccess('Tax code deleted');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to delete tax code');
    } finally {
      setDeleting(null);
    }
  };

  const codePicker = (lbl: string, field: 'defaultOutputCodeId' | 'defaultInputCodeId' | 'exportCodeId', filter: (c: TaxCode) => boolean, tag: Record<string, string>) => (
    <Box sx={{ flex: '1 1 260px', maxWidth: 380 }}>
      <SearchableSelect<TaxCode>
        {...tag}
        label={lbl}
        options={codes.filter(c => c.active && filter(c))}
        value={profile[field] ?? ''}
        onChange={id => setP({ [field]: id || null })}
        getOptionLabel={c => `${c.code} - ${c.name}`}
        placeholder="None"
        disabled={!canManage}
        size="small"
        data-testid={`profile-${field}`}
      />
    </Box>
  );
  const acctPicker = (lbl: string, field: 'settlementAccountId' | 'refundableAccountId' | 'nonRecoverableExpenseAccountId', options: Account[]) => (
    <Box sx={{ flex: '1 1 260px', maxWidth: 380 }}>
      <AccountAutocomplete accounts={options} value={profile[field] ?? ''} onChange={id => setP({ [field]: id || null })}
        label={lbl} placeholder="None" disabled={!canManage} size="small" />
    </Box>
  );

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box data-help-page="tax.codes.page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>VAT / Tax Codes</Typography>

      <Paper data-help-section="tax.codes.profile" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Tax profile</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: '1 1 200px', maxWidth: 280 }}>
            <TextField data-help="tax.codes.regime" select fullWidth size="small" label="Regime" value={profile.regime} disabled={!canManage}
              onChange={e => setP({ regime: e.target.value as TaxRegime })}>
              <MenuItem value="NETTING">Netting (output minus input)</MenuItem>
              <MenuItem value="SINGLE_STAGE">Single stage</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 140px', maxWidth: 200 }}>
            <TextField data-help="tax.codes.country" fullWidth size="small" label="Country code" value={profile.countryCode} disabled={!canManage}
              onChange={e => setP({ countryCode: e.target.value.toUpperCase() })} inputProps={{ maxLength: 2 }} />
          </Box>
          <Box sx={{ flex: '1 1 180px', maxWidth: 240 }}>
            <TextField data-help="tax.codes.frequency" select fullWidth size="small" label="Filing frequency" value={profile.filingFrequency} disabled={!canManage}
              onChange={e => setP({ filingFrequency: e.target.value as TaxFilingFrequency })}>
              <MenuItem value="MONTHLY">Monthly</MenuItem>
              <MenuItem value="QUARTERLY">Quarterly</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
            <TextField data-help="tax.codes.bin" fullWidth size="small" label="BIN / registration no." value={profile.registrationNo ?? ''} disabled={!canManage}
              onChange={e => setP({ registrationNo: e.target.value })} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          {codePicker('Default output code', 'defaultOutputCodeId', c => c.applicability !== 'INPUT', { 'data-help': 'tax.codes.profile.field.defaultOutput' })}
          {codePicker('Default input code', 'defaultInputCodeId', c => c.applicability !== 'OUTPUT', { 'data-help': 'tax.codes.profile.field.defaultInput' })}
          {codePicker('Export code', 'exportCodeId', c => c.applicability !== 'INPUT', { 'data-help': 'tax.codes.profile.field.export' })}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          {acctPicker('Settlement (VAT payable) account', 'settlementAccountId', liabilityAccts)}
          {acctPicker('Refundable (VAT receivable) account', 'refundableAccountId', assetAccts)}
          {acctPicker('Non-recoverable tax expense account', 'nonRecoverableExpenseAccountId', expenseAccts)}
        </Box>
        {canManage && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button data-help="tax.codes.saveProfile" variant="contained" onClick={saveProfile} disabled={savingProfile}>Save profile</Button>
          </Box>
        )}
      </Paper>

      <Box data-help-section="tax.codes.toolbar" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ flex: '1 1 200px' }}>Tax codes</Typography>
        {canManage && (
          <>
            <Button data-help="tax.codes.loadDefaults" variant="outlined" onClick={() => setConfirmDefaults(true)}>Load Bangladesh defaults</Button>
            <Button data-help="tax.codes.add" variant="contained" startIcon={<AddIcon />} onClick={() => openDialog(null)}>Add tax code</Button>
          </>
        )}
      </Box>

      {defaultsResult && (
        <Alert severity={defaultsResult.warnings.length ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setDefaultsResult(null)}>
          Created {defaultsResult.created}, updated {defaultsResult.updated}, unchanged {defaultsResult.unchanged},
          skipped (edited) {defaultsResult.skippedEdited}.
          {defaultsResult.warnings.map((w, i) => <div key={i}>{w}</div>)}
        </Alert>
      )}

      <Paper sx={{ overflowX: 'auto' }}>
        <Table data-help-section="tax.codes.table" size="small">
          <TableHead>
            <TableRow>
              <TableCell data-help="tax.codes.col.code">Code</TableCell><TableCell data-help="tax.codes.col.name">Name</TableCell><TableCell data-help="tax.codes.col.category">Category</TableCell>
              <TableCell data-help="tax.codes.col.applies">Applies to</TableCell><TableCell data-help="tax.codes.col.recoverable">Recoverable</TableCell>
              <TableCell data-help="tax.codes.col.rate" align="right">Effective rate</TableCell><TableCell data-help="tax.codes.col.components">Components</TableCell>
              <TableCell data-help="tax.codes.col.status">Status</TableCell><TableCell data-help="tax.codes.col.actions" align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {codes.length === 0 && (
              <TableRow><TableCell colSpan={9}><Typography color="text.secondary">No tax codes yet. Load the Bangladesh defaults or add one.</Typography></TableCell></TableRow>
            )}
            {codes.map(c => (
              <TableRow key={c.id} hover>
                <TableCell>{c.code}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{label(c.category)}</TableCell>
                <TableCell>{label(c.applicability)}</TableCell>
                <TableCell>{c.recoverable ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">{c.effectiveRatePct}%</TableCell>
                <TableCell>{componentSummary(c)}</TableCell>
                <TableCell>
                  <Chip size="small" label={c.active ? 'Active' : 'Inactive'} color={c.active ? 'success' : 'default'} sx={{ mr: 0.5 }} />
                  {c.edited && <Chip size="small" label="Edited" color="info" variant="outlined" />}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {canManage && (
                    <>
                      <Button data-help="tax.codes.action.edit" size="small" onClick={() => openDialog(c)}>Edit</Button>
                      <Button data-help="tax.codes.action.toggle" size="small" onClick={() => toggleActive(c)}>{c.active ? 'Deactivate' : 'Activate'}</Button>
                      <Tooltip title="Delete (refused if the code is in use)">
                        <IconButton data-help="tax.codes.action.delete" size="small" aria-label={`Delete ${c.code}`} onClick={() => setDeleting(c)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <ConfirmDialog
        open={confirmDefaults}
        title="Load Bangladesh defaults"
        message="This is opt-in and idempotent: it creates the standard Bangladesh VAT / SD tax codes for this institution, and running it again changes nothing that is already current. Codes you have edited are never overwritten."
        confirmLabel="Load defaults"
        submitting={loadingDefaults}
        onCancel={() => setConfirmDefaults(false)}
        onConfirm={runDefaults}
      />
      <ConfirmDialog
        open={!!deleting}
        title="Delete tax code"
        message={`Delete tax code ${deleting?.code ?? ''}? The server refuses this when the code is already used on documents; deactivate it instead.`}
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setDeleting(null)}
        onConfirm={doDelete}
      />

      <Dialog data-help-page="tax.codes.form.page" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit tax code' : 'Add tax code'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 140px', maxWidth: 200 }}>
                <TextField data-help="tax.codes.form.field.code" fullWidth size="small" required label="Code" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} />
              </Box>
              <Box sx={{ flex: '1 1 240px', maxWidth: 400 }}>
                <TextField data-help="tax.codes.form.field.name" fullWidth size="small" required label="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </Box>
              <Box sx={{ flex: '1 1 160px', maxWidth: 220 }}>
                <TextField data-help="tax.codes.form.field.category" select fullWidth size="small" label="Category" value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value as TaxCategory }))}>
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{label(c)}</MenuItem>)}
                </TextField>
              </Box>
              <Box sx={{ flex: '1 1 140px', maxWidth: 200 }}>
                <TextField data-help="tax.codes.form.field.applies" select fullWidth size="small" label="Applies to" value={form.applicability}
                  onChange={e => setForm(f => ({ ...f, applicability: e.target.value as TaxApplicability }))}>
                  {APPLICABILITIES.map(c => <MenuItem key={c} value={c}>{label(c)}</MenuItem>)}
                </TextField>
              </Box>
            </Box>
            <FormControlLabel data-help="tax.codes.form.field.recoverable" control={<Checkbox checked={form.recoverable} onChange={e => setForm(f => ({ ...f, recoverable: e.target.checked }))} />}
              label="Input tax is recoverable (claimable against output tax)" />
            {!form.recoverable && (
              <Alert severity="info">Non-recoverable input tax is not claimable: it is booked as a cost (expense) of the purchase.</Alert>
            )}
            <TextField data-help="tax.codes.form.field.note" size="small" label="Note" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />

            <Typography variant="subtitle2">Components (applied in order)</Typography>
            {comps.map((c, i) => (
              <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Box sx={{ flex: '1 1 140px', maxWidth: 200 }}>
                  <TextField data-help="tax.codes.form.comp.name" fullWidth size="small" required label="Component name" value={c.name} onChange={e => setComp(i, { name: e.target.value })} />
                </Box>
                <Box sx={{ flex: '1 1 100px', maxWidth: 130 }}>
                  <TextField data-help="tax.codes.form.comp.rate" fullWidth size="small" required type="number" label="Rate %" value={c.ratePct}
                    inputProps={{ min: 0, step: 'any' }} onChange={e => setComp(i, { ratePct: e.target.value })} />
                </Box>
                <Box sx={{ flex: '1 1 150px', maxWidth: 200 }}>
                  <TextField data-help="tax.codes.form.comp.kind" select fullWidth size="small" label="Kind" value={c.kind}
                    onChange={e => setComp(i, { kind: e.target.value as TaxComponentKind })}>
                    <MenuItem value="VAT">VAT</MenuItem>
                    <MenuItem value="SUPPLEMENTARY_DUTY">Supplementary duty</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </TextField>
                </Box>
                <FormControlLabel data-help="tax.codes.form.comp.compound" sx={{ flex: '0 0 auto' }} label="Compound on previous"
                  control={<Checkbox checked={i > 0 && c.compoundOnPrevious} disabled={i === 0} onChange={e => setComp(i, { compoundOnPrevious: e.target.checked })} />} />
                <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
                  <AccountAutocomplete data-help="tax.codes.form.comp.output" accounts={liabilityAccts} value={c.outputAccountId} onChange={id => setComp(i, { outputAccountId: id })}
                    label="Output account (liability)" size="small" placeholder="None" />
                </Box>
                <Box sx={{ flex: '1 1 220px', maxWidth: 320 }}>
                  <AccountAutocomplete data-help="tax.codes.form.comp.input" accounts={form.recoverable ? assetAccts : expenseAccts} value={c.inputAccountId}
                    onChange={id => setComp(i, { inputAccountId: id })}
                    label={form.recoverable ? 'Input account (asset)' : 'Input account (expense)'} size="small" placeholder="None" />
                </Box>
                <IconButton data-help="tax.codes.action.removeComponent" size="small" aria-label="Remove component" disabled={comps.length === 1}
                  onClick={() => setComps(cs => cs.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Box>
            ))}
            <Box><Button data-help="tax.codes.form.addComponent" size="small" startIcon={<AddIcon />} onClick={() => setComps(cs => [...cs, emptyComp()])}>Add component</Button></Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button data-help="tax.codes.form.cancel" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button data-help="tax.codes.form.save" variant="contained" onClick={saveCode} disabled={!formValid || saving}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaxCodesPage;
